import { memo, useCallback, useId, useMemo, type ComponentType } from 'react';

import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material';

import { Box } from '@/ui/layout';
import { Button } from '@/ui/button/button';
import { Collapse } from '@/ui/collapse/collapse';
import { List } from '@/ui/list/list';
import { getSxClasses } from './legend-styles';
import { ItemsList } from './legend-layer-items';
import type { LegendLayerProps } from './legend-layer';
import { logger } from '@/core/utils/logger';
import { layerHasClassItems, layerHasLegendImage } from '@/core/components/layers/types';
import type { TypeContainerBox } from '@/core/types/global-types';
import { useStoreGeoViewMapId } from '@/core/stores/geoview-store';
import {
  useStoreLayerChildPaths,
  useStoreLayerIcons,
  useStoreLayerItems,
  useStoreLayerName,
  useStoreLayerStatus,
  useStoreLayerSchemaTag,
  useStoreLayerLegendCollapsed,
  useStoreLayerStyleConfig,
} from '@/core/stores/states/layer-state';

interface CollapsibleContentProps {
  layerPath: string;
  initLightBox: (images: string, altText: string, returnFocusId: string, index?: number) => void;
  LegendLayerComponent: ComponentType<LegendLayerProps>;
  showControls: boolean;
  containerType: TypeContainerBox;
  collapseContainerId: string;
  layerNameId: string;
}

interface WMSLegendImageProps {
  imgSrc: string;
  title?: string;
  initLightBox: (images: string, altText: string, returnFocusId: string, index?: number) => void;
  legendExpanded: boolean;
  sxClasses: Record<string, object>;
  mapId: string;
  containerType: TypeContainerBox;
  collapseContainerId: string;
}

/**
 * Renders a WMS legend image with lightbox support.
 *
 * Memoized to avoid re-rendering when parent CollapsibleContent re-renders due to unrelated state changes.
 */
const WMSLegendImage = memo(
  ({
    imgSrc,
    initLightBox,
    legendExpanded,
    sxClasses,
    title,
    mapId,
    containerType,
    collapseContainerId,
  }: WMSLegendImageProps): JSX.Element => {
    // Log
    logger.logTraceRender('components/legend/legend-layer-container - WMSLegendImage');

    const { t } = useTranslation<string>();
    const id = useId();
    const buttonId = `${mapId}-${containerType}-legend-image-btn-${id}`; // Create unique ID for focus management after lightbox closes
    const altText = title ? `${t('legend.title')}, ${title}` : t('legend.title');

    /**
     * Handles when the user clicks the legend image to open the lightbox.
     */
    const handleClick = useCallback((): void => {
      initLightBox(imgSrc, altText, buttonId, 0);
    }, [initLightBox, imgSrc, altText, buttonId]);

    return (
      <Collapse id={collapseContainerId} in={legendExpanded} sx={sxClasses.collapsibleContainer} timeout="auto">
        <Button
          type="icon"
          sx={sxClasses.imageButton}
          id={buttonId}
          onClick={handleClick}
          tooltip={t('general.enlargeImage')}
          tooltipPlacement="top"
          aria-label={title ? t('general.enlargeImageName', { title }) : t('general.enlargeImage')} // WCAG - Descriptive aria-label for screen readers
          disableRipple
        >
          <Box component="img" src={imgSrc} alt={altText} sx={sxClasses.wmsImage} />
        </Button>
      </Collapse>
    );
  }
);
WMSLegendImage.displayName = 'WMSLegendImage';

export const CollapsibleContent = memo(
  ({
    layerPath,
    initLightBox,
    LegendLayerComponent,
    showControls,
    containerType,
    collapseContainerId,
    layerNameId,
  }: CollapsibleContentProps): JSX.Element | null => {
    // Log
    logger.logTraceRender('components/legend/legend-layer-container - CollapsibleContent', layerPath);

    // Hooks
    const mapId = useStoreGeoViewMapId();
    const theme = useTheme();
    const memoSxClasses = useMemo(() => {
      logger.logTraceUseMemo('components/legend/legend-layer-container - CollapsibleContent - sxClasses', theme);
      return getSxClasses(theme);
    }, [theme]);
    const isCollapsed = useStoreLayerLegendCollapsed(layerPath);
    const schemaTag = useStoreLayerSchemaTag(layerPath);
    const layerItems = useStoreLayerItems(layerPath);
    const layerStyleConfig = useStoreLayerStyleConfig(layerPath);
    const layerChildPaths = useStoreLayerChildPaths(layerPath);
    const layerIcons = useStoreLayerIcons(layerPath);
    const layerStatus = useStoreLayerStatus(layerPath);
    const layerName = useStoreLayerName(layerPath);

    // Has at least 2 layer items and style config
    const hasMoreThanOneItemsAndStyle = layerHasClassItems(layerItems, layerStyleConfig, 2);

    // If the layer has a legend image
    const hasLegendImage = layerHasLegendImage(schemaTag, layerItems, layerIcons, layerStyleConfig);

    // If the layer has child layers
    const hasChildren = layerChildPaths && layerChildPaths.length > 0;

    // TODO: PERFORMANCE - Early return when the no child or when layer items is 1 or when error. Search id: 39c51cfc
    if ((layerChildPaths?.length === 0 && layerItems?.length === 1) || layerStatus === 'error') return null;

    // If it is a WMS legend, create a specific component
    if (hasMoreThanOneItemsAndStyle || hasChildren) {
      return (
        <Collapse
          id={collapseContainerId}
          role="region" // WCAG - aria-labelledby requires the region role to be announced by screen readers
          aria-labelledby={layerNameId} // WCAG - Link collapsible content to its header using aria-labelledby and matching IDs
          in={!isCollapsed}
          sx={memoSxClasses.collapsibleContainer}
          timeout="auto"
          unmountOnExit
        >
          {hasChildren && (
            <List>
              {layerChildPaths.map((childPath) => (
                <LegendLayerComponent layerPath={childPath} key={childPath} showControls={showControls} containerType={containerType} />
              ))}
            </List>
          )}
          <ItemsList items={layerItems || []} layerPath={layerPath} />
        </Collapse>
      );
    }

    if (hasLegendImage) {
      return (
        <WMSLegendImage
          imgSrc={layerIcons![0].iconImage!}
          initLightBox={initLightBox}
          legendExpanded={!isCollapsed}
          sxClasses={memoSxClasses}
          title={layerName}
          mapId={mapId}
          containerType={containerType}
          collapseContainerId={collapseContainerId}
        />
      );
    }

    return null;
  }
);
CollapsibleContent.displayName = 'CollapsibleContent';
